import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import styles from "./WordGame.module.css";
import logo from "../../assets/images/LightLogo.webp";

type LetterState = "correct" | "present" | "absent";

type GuessedWord = {
  letters: string[];
  states: LetterState[];
};

type GameConfig = {
  WORD_LENGTH: number;
  MAX_ATTEMPTS: number;
  DIGRAPHS: Set<string>;
  DAILY_WORD: string | null;
  PRODUCTION_MODE: boolean;
};

type SavedGameState = {
  targetWord: string;
  currentAttempt: number;
  currentGuess: string[];
  guessedWords: GuessedWord[];
  usedLetters: string[];
  gameComplete: boolean;
  savedAt: string;
  dayKey: string;
  dailyWord: string | null;
};

const CONFIG: GameConfig = {
  WORD_LENGTH: 5,
  MAX_ATTEMPTS: 6,
  DIGRAPHS: new Set(["dh", "gj", "ll", "nj", "rr", "sh", "th", "xh", "zh"]),
  DAILY_WORD: null,
  PRODUCTION_MODE: false,
};

const STORAGE_KEY = "fjalz_game_state";

function getDayKey(): string {
  const today = new Date();
  return `${today.getUTCFullYear()}-${today.getUTCMonth() + 1}-${today.getUTCDate()}`;
}

function parseTextWithDigraphs(text: string): string[] {
  const result: string[] = [];
  let i = 0;
  while (i < text.length) {
    if (i < text.length - 1) {
      const twoChar = text.substring(i, i + 2).toLowerCase();
      if (CONFIG.DIGRAPHS.has(twoChar)) {
        result.push(twoChar);
        i += 2;
        continue;
      }
    }
    result.push(text[i].toLowerCase());
    i++;
  }
  return result;
}

function selectDailyWord(wordList: string[]): string {
  if (CONFIG.DAILY_WORD && wordList.includes(CONFIG.DAILY_WORD)) {
    return CONFIG.DAILY_WORD;
  }

  const today = new Date();
  const dateSeed = `${today.getUTCFullYear()}-${today.getUTCMonth() + 1}-${today.getUTCDate()}`;

  let shuffleSeed = 0;
  for (let i = 0; i < dateSeed.length; i++) {
    shuffleSeed = (shuffleSeed << 5) - shuffleSeed + dateSeed.charCodeAt(i);
    shuffleSeed |= 0;
  }

  const shuffledWords = [...wordList];
  for (let i = shuffledWords.length - 1; i > 0; i--) {
    shuffleSeed = (shuffleSeed * 1664525 + 1013904223) >>> 0;
    const j = Math.floor((shuffleSeed / 0xffffffff) * (i + 1));
    [shuffledWords[i], shuffledWords[j]] = [shuffledWords[j], shuffledWords[i]];
  }

  return shuffledWords[0];
}

export default function OnlineKosovaWord() {
  const navigate = useNavigate();
  const [wordList, setWordList] = useState<string[]>([]);
  const [targetWord, setTargetWord] = useState<string>("");
  const [currentGuess, setCurrentGuess] = useState<string[]>([]);
  const [guessedWords, setGuessedWords] = useState<GuessedWord[]>([]);
  const [currentAttempt, setCurrentAttempt] = useState<number>(0);
  const [gameComplete, setGameComplete] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [usedLetters, setUsedLetters] = useState<Set<string>>(new Set());
  const [showGameOver, setShowGameOver] = useState<boolean>(false);

  useEffect(() => {
    const loadWords = async () => {
      try {
        const res = await fetch("/brieffo.txt");
        const text = await res.text();
        const words = text
          .split("\n")
          .map((w) => w.trim().toLowerCase())
          .filter((w) => {
            const parsed = parseTextWithDigraphs(w);
            return parsed.length === CONFIG.WORD_LENGTH && /^[a-zçë]+$/.test(w);
          });
        setWordList(words);

        const word = selectDailyWord(words);

        const savedState = loadGameState();
        if (
          savedState &&
          savedState.dayKey === getDayKey() &&
          savedState.targetWord === word
        ) {
          setTargetWord(savedState.targetWord);
          setCurrentGuess(savedState.currentGuess);
          setGuessedWords(savedState.guessedWords);
          setCurrentAttempt(savedState.currentAttempt);
          setGameComplete(savedState.gameComplete);
          setShowGameOver(savedState.gameComplete);
          setUsedLetters(new Set(savedState.usedLetters));
        } else {
          clearSavedGame();
          setTargetWord(word);
        }
      } catch (err) {
        console.error("Failed to load word list", err);
        const fallback = ["fjala", "mësim", "çelës", "bukur", "ëndër"];
        setWordList(fallback);
        const word = selectDailyWord(fallback);

        setTargetWord(word);
      }
    };
    loadWords();
    initAntiTamper();
  }, []);

  useEffect(() => {
    if (targetWord && wordList.length > 0) {
      saveGameState();
    }
  }, [
    currentGuess,
    guessedWords,
    currentAttempt,
    gameComplete,
    targetWord,
    wordList,
  ]);

  function saveGameState() {
    const stateToSave: SavedGameState = {
      targetWord,
      currentAttempt,
      currentGuess,
      guessedWords,
      usedLetters: Array.from(usedLetters),
      gameComplete,
      savedAt: new Date().toISOString(),
      dayKey: getDayKey(),
      dailyWord: CONFIG.DAILY_WORD,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }

  function loadGameState(): SavedGameState | null {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (!savedState) return null;

    try {
      return JSON.parse(savedState) as SavedGameState;
    } catch (e) {
      console.error("Failed to load game state", e);
      return null;
    }
  }

  function clearSavedGame() {
    localStorage.removeItem(STORAGE_KEY);
  }

  const handleKeyPress = (key: string) => {
    if (gameComplete) return;
    if (currentAttempt >= CONFIG.MAX_ATTEMPTS) return;

    if (key === "enter") {
      submitGuess();
    } else if (key === "backspace") {
      setCurrentGuess((prev) => prev.slice(0, -1));
    } else if (key.length === 2 && CONFIG.DIGRAPHS.has(key.toLowerCase())) {
      const normalized = parseTextWithDigraphs([...currentGuess, key].join(""));
      if (normalized.length <= CONFIG.WORD_LENGTH) {
        setCurrentGuess((prev) => [...prev, key.toLowerCase()]);
      }
    } else if (/^[a-zçë]$/i.test(key)) {
      const normalized = parseTextWithDigraphs([...currentGuess, key].join(""));
      if (normalized.length <= CONFIG.WORD_LENGTH) {
        setCurrentGuess((prev) => [...prev, key.toLowerCase()]);
      }
    }
  };

  const submitGuess = () => {
    if (gameComplete) return;

    const letters = parseTextWithDigraphs(currentGuess.join(""));

    if (letters.length !== CONFIG.WORD_LENGTH) {
      setErrorMessage("Duhet 5 shkronja!");
      setTimeout(() => setErrorMessage(""), 2000);
      return;
    }

    const guessStr = letters.join("");
    if (!wordList.includes(guessStr)) {
      setErrorMessage("Nuk është fjalë valide!");
      setTimeout(() => setErrorMessage(""), 2000);
      return;
    }

    setErrorMessage("");
    const targetLetters = parseTextWithDigraphs(targetWord);
    const states: LetterState[] = Array(CONFIG.WORD_LENGTH).fill("absent");
    const letterStates: Record<string, LetterState> = {};
    const targetCopy = [...targetLetters];

    letters.forEach((l, i) => {
      if (l === targetCopy[i]) {
        states[i] = "correct";
        letterStates[l] = "correct";
        targetCopy[i] = "";
      }
    });

    letters.forEach((l, i) => {
      if (states[i] === "correct") return;
      const idx = targetCopy.indexOf(l);
      if (idx !== -1) {
        states[i] = "present";
        if (letterStates[l] !== "correct") {
          letterStates[l] = "present";
        }
        targetCopy[idx] = "";
      } else {
        if (!letterStates[l]) {
          letterStates[l] = "absent";
        }
      }
    });

    const newGuessed: GuessedWord = { letters, states };
    setGuessedWords((prev) => [...prev, newGuessed]);
    setUsedLetters((prev) => {
      const newSet = new Set(prev);
      Object.keys(letterStates).forEach((k) => newSet.add(k));
      return newSet;
    });

    if (guessStr === targetWord) {
      setGameComplete(true);
      setShowGameOver(true);
    } else if (currentAttempt + 1 >= CONFIG.MAX_ATTEMPTS) {
      setGameComplete(true);
      setShowGameOver(true);
      setCurrentAttempt(currentAttempt + 1);
      setCurrentGuess([]);
    } else {
      setCurrentAttempt(currentAttempt + 1);
      setCurrentGuess([]);
    }
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (gameComplete) {
        e.preventDefault();
        return;
      }
      if (currentAttempt >= CONFIG.MAX_ATTEMPTS) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const key = e.key.toLowerCase();
      if (key === "enter" || key === "backspace" || /^[a-zçë]$/.test(key)) {
        e.preventDefault();
        handleKeyPress(key);
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [currentGuess, gameComplete, currentAttempt]);

  useEffect(() => {
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (window.scrollY === 0 && e.touches[0].clientY > touchStartY + 150) {
        window.location.reload();
      }
    };

    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  const renderGrid = () => {
    const rows = [];
    for (let r = 0; r < CONFIG.MAX_ATTEMPTS; r++) {
      const tiles = [];

      if (r < guessedWords.length) {
        const guess = guessedWords[r];
        for (let c = 0; c < CONFIG.WORD_LENGTH; c++) {
          const letter = guess.letters[c] || "";
          const state = guess.states[c] || "empty";
          tiles.push(
            <div
              key={c}
              className={`${styles.cell} ${state ? styles[state] : ""}`}
            >
              {letter.toUpperCase()}
            </div>,
          );
        }
      } else if (r === currentAttempt) {
        const parsed = parseTextWithDigraphs(currentGuess.join(""));
        for (let c = 0; c < CONFIG.WORD_LENGTH; c++) {
          const letter = parsed[c] || "";
          tiles.push(
            <div
              key={c}
              className={`${styles.cell} ${letter ? styles.filled : ""}`}
            >
              {letter.toUpperCase()}
            </div>,
          );
        }
      } else {
        for (let c = 0; c < CONFIG.WORD_LENGTH; c++) {
          tiles.push(<div key={c} className={`${styles.cell}`}></div>);
        }
      }

      rows.push(
        <div key={r} className={styles.row}>
          {tiles}
        </div>,
      );
    }
    return rows;
  };

  const getKeyState = (key: string): LetterState | null => {
    let bestState: LetterState | null = null;
    for (let i = 0; i < guessedWords.length; i++) {
      const letters = guessedWords[i].letters;
      const states = guessedWords[i].states;
      const keyIndex = letters.findIndex(
        (l) => l.toLowerCase() === key.toLowerCase(),
      );
      if (keyIndex !== -1) {
        const state = states[keyIndex];
        if (state === "correct") return "correct";
        if (state === "present" && bestState !== "correct")
          bestState = "present";
        if (state === "absent" && !bestState) bestState = "absent";
      }
    }
    return bestState;
  };

  function initAntiTamper() {
    if (!CONFIG.PRODUCTION_MODE) return;

    try {
      const silent = function () {};
      console.log = silent;
      console.info = silent;
      console.warn = silent;
      console.error = silent;
      console.debug = silent;
    } catch (_) {}

    document.addEventListener(
      "contextmenu",
      function (e) {
        e.preventDefault();
      },
      { capture: true },
    );

    document.addEventListener(
      "keydown",
      function (e) {
        const key = (e.key || "").toLowerCase();
        if (
          key === "f12" ||
          (e.ctrlKey &&
            e.shiftKey &&
            (key === "i" || key === "j" || key === "c")) ||
          (e.ctrlKey && (key === "u" || key === "s"))
        ) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { capture: true },
    );

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
      let devtoolsTripped = false;
      function detectDevTools() {
        try {
          const threshold = 160;
          const widthDiff = Math.abs(window.outerWidth - window.innerWidth);
          const heightDiff = Math.abs(window.outerHeight - window.innerHeight);
          const isOpen = widthDiff > threshold || heightDiff > threshold;
          if (isOpen && !devtoolsTripped) {
            devtoolsTripped = true;
            document.documentElement.innerHTML = "";
          }
        } catch (_) {}
      }
      setInterval(detectDevTools, 800);
    }
  }

  const keyboard = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Ç", "Z", "X", "C", "V", "B", "N", "M", "Ë", "⌫"],
  ];

  const handleKeyClick = (key: string) => {
    if (gameComplete) return;
    if (currentAttempt >= CONFIG.MAX_ATTEMPTS) return;

    if (key === "ENTER") {
      submitGuess();
    } else if (key === "⌫") {
      handleKeyPress("backspace");
    } else if (CONFIG.DIGRAPHS.has(key.toLowerCase())) {
      handleKeyPress(key.toLowerCase());
    } else {
      handleKeyPress(key.toLowerCase());
    }
  };

  const isWin = guessedWords.some((g) => g.letters.join("") === targetWord);
  const attemptsUsed = guessedWords.length;

  return (
    <>
      <div className={styles.info}>
        <div className={styles.backRow} onClick={() => navigate("/lajmet")}>
          <div className={styles.circleButton}>
            <ArrowLeftOutlined style={{ color: "white", fontSize: "1.1rem" }} />
          </div>
          <h2
            className={styles.backText}
            style={{ fontFamily: "Clash Display" }}
          >
            Rikthehuni ne faqe
          </h2>
        </div>
        <div style={{ flex: 1 }}></div>
        <button onClick={() => setShowInfo(true)}>?</button>
      </div>
      <div className={styles.container}>
        <header className={styles.header}>
          <span
            style={{
              fontFamily: "Clash Display",
            }}
          >
            <img src={logo} className={styles.brandLogo} alt="logo" />
          </span>
        </header>

        {showInfo && (
          <div className={styles.gameInfo}>
            <button
              className={styles.closeButton}
              onClick={() => setShowInfo(false)}
            >
              ×
            </button>
            <h3>Informacione</h3>
            <p>
              {" "}
              * Secila përgjigje duhet të jetë një fjalë e vlefshme me 5
              shkronja
            </p>
            <p>
              {" "}
              * Pas çdo përpjekjeje, ngjyra e kutive do të ndryshojë për të
              treguar sa afër ishit me fjalën
            </p>
            <h4>Kuptimi i Ngjyrave</h4>
            <p>
              🟦 Blu (Saktë) - Shkronja është në fjalë dhe në pozicionin e saktë
            </p>
            <p>
              🟨 Verdhë (Prezent) - Shkronja është në fjalë por në pozicionin e
              gabuar
            </p>
            <p>⬛ Gri (Mungon) - Shkronja nuk është në fjalë</p>
          </div>
        )}

        {gameComplete && showGameOver && (
          <div className={styles.gameOver}>
            <button
              className={styles.closeButton}
              onClick={() => setShowGameOver(false)}
            >
              ×
            </button>
            {isWin ? (
              <div className={styles.winMessage}>
                <h2>🎉 Urime! 🎉</h2>
                <p>
                  E gjetët fjalën në {attemptsUsed}{" "}
                  {attemptsUsed === 1 ? "tentativë" : "tentativa"}!
                </p>
              </div>
            ) : (
              <div className={styles.loseMessage}>
                <h2>Loja Mbaroi!</h2>
                <p>
                  Fjala ishte: <strong>{targetWord}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        <div className={styles.gameBoard}>
          {errorMessage && (
            <div className={styles.errorMessage}>{errorMessage}</div>
          )}
          {renderGrid()}
        </div>

        <div className={styles.keyboard}>
          {keyboard.map((row, i) => (
            <div key={i} className={styles.keyboardRow}>
              {row.map((key) => {
                const keyState = getKeyState(key);
                return (
                  <button
                    key={key}
                    onClick={() => handleKeyClick(key)}
                    className={`${styles.key} ${
                      key === "ENTER" ||
                      key === "⌫" ||
                      CONFIG.DIGRAPHS.has(key.toLowerCase())
                        ? styles.specialKey
                        : ""
                    } ${keyState ? styles[keyState] : ""}`}
                    disabled={gameComplete}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
